import { gql, useMutation, useQuery } from "@apollo/client";
import "ag-grid-community/styles/ag-grid.css";
import { AgGridReact } from "ag-grid-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { arrow_left, arrow_right, copy, download, eye, maximize2, trash, xIcon } from "../../../images/NewDesign";
import { theme } from "../../../utils/theme";
import { appToast, errorToast } from "../../../utils/toast";
import { environment } from "../../../utils/variables";
import { Modal } from "../../modal/Modal";
import { AppText, FlexDiv } from "../../UI";
import { PhoenixAppButton, PhoenixDateRange, PhoenixIcon, PhoenixInput, PhoenixTab } from "../../UI/Phoenix";

const FETCH_API_TOKENS = gql`
  query fetchPublicApiTokens($take: Int, $skip: Int) {
    fetchPublicApiTokens(take: $take, skip: $skip) {
      id
      token
      type
      expires_at
      last_used_at
      name
      description
      created_at
      updated_at
    }
  }
`;

const FETCH_API_REQUEST_LOGS = gql`
  query FetchPublicApiRequestLogs(
    $take: Int = 10
    $skip: Int = 0
    $orderBy: PrismaSortOrder = desc
    $start_date: DateTime
    $end_date: DateTime
  ) {
    publicApiRequestLogs(take: $take, skip: $skip, order_by: $orderBy, start_date: $start_date, end_date: $end_date) {
      id
      endpoint
      created_at
      method
      request_body_safe
      response_body_safe
      response_code
      token_id
      execution_time
    }
  }
`;

const FETCH_API_REQUEST_LOG_COUNT = gql`
  query FetchPublicApiRequestLogCount {
    publicApiRequestLogCount
  }
`;

const DELETE_API_TOKEN = gql`
  mutation deletePublicApiToken($id: String!) {
    deletePublicApiToken(id: $id)
  }
`;

const CREATE_API_TOKEN = gql`
  mutation createPublicApiToken($api_key_type: ApiKeyType!, $name: String, $description: String) {
    createPublicApiToken(api_key_type: $api_key_type, name: $name, description: $description)
  }
`;

// Add GraphQL queries and mutations for webhooks
const FETCH_WEBHOOK_CONFIG = gql`
  query FetchWebhookConfig {
    fetchWebhookConfig {
      id
      organization_id
      webhook_url
      webhook_secret
      created_at
      updated_at
      deleted_at
    }
  }
`;

const CREATE_OR_UPDATE_WEBHOOK_CONFIG = gql`
  mutation CreateOrUpdateWebhookConfig($webhook_url: String!, $webhook_secret: String!) {
    createOrUpdateWebhookConfig(webhook_url: $webhook_url, webhook_secret: $webhook_secret) {
      id
      organization_id
      webhook_url
      webhook_secret
      created_at
      updated_at
    }
  }
`;

const CREATE_OR_UPDATE_WEBHOOK_EVENT_SUBSCRIPTION = gql`
  mutation CreateOrUpdateWebhookEventSubscription($event_type: WebHookEventType!, $fields: [String!]!) {
    createOrUpdateWebhookEventSubscription(event_type: $event_type, fields: $fields) {
      id
      organization_id
      event_type
      fields
      created_at
      updated_at
    }
  }
`;

const FETCH_WEBHOOK_EVENT_SUBSCRIPTIONS = gql`
  query FetchWebhookEventSubscriptions {
    fetchWebhookEventSubscriptions {
      id
      organization_id
      event_type
      fields
      created_at
      updated_at
    }
  }
`;

// Add WebHookEventType enum
enum WebHookEventType {
  LeadUpdated = "LeadUpdated",
  // LeadUpdatedSpecificSystemFields = "LeadUpdatedSpecificSystemFields", // Commented out - not supported for now
  LeadCreated = "LeadCreated",
  LeadActivityCreated = "LeadActivityCreated",
  LeadActivityDemoSetCreated = "LeadActivityDemoSetCreated",
  LeadActivityDemoReescheduled = "LeadActivityDemoReescheduled",
  LeadActivityNewCallNote = "LeadActivityNewCallNote",
  LeadActivityNewSMS = "LeadActivityNewSMS",
  LeadActivityNewEmail = "LeadActivityNewEmail",
}

const FETCH_WEBHOOK_LOGS = gql`
  query FetchWebhookLogs($take: Int = 10, $skip: Int = 0, $order_by: PrismaSortOrder = desc) {
    fetchWebhookLogs(take: $take, skip: $skip, order_by: $order_by) {
      id
      request_body
      response_body
      response_code
      event_types
      organization_id
      method
      url
      created_at
      updated_at
    }
  }
`;

const FETCH_WEBHOOK_LOGS_COUNT = gql`
  query FetchWebhookLogsCount {
    fetchWebhookLogsCount
  }
`;

// Add the DELETE_WEBHOOK_EVENT_SUBSCRIPTION mutation
const DELETE_WEBHOOK_EVENT_SUBSCRIPTION = gql`
  mutation DeleteWebhookEventSubscription($id: String!) {
    deleteWebhookEventSubscription(id: $id)
  }
`;

// Add ExportPublicApiRequestLogs mutation
const EXPORT_PUBLIC_API_REQUEST_LOGS = gql`
  mutation ExportPublicApiRequestLogs($start_date: DateTime!, $end_date: DateTime!, $order_by: PrismaSortOrder = desc) {
    exportPublicApiRequestLogs(start_date: $start_date, end_date: $end_date, order_by: $order_by)
  }
`;

// Styled components for cell renderers
const ApiKeyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  width: 100%;
`;

const ApiKeyText = styled.div`
  font-family: "Roboto Mono", monospace;
  font-size: 12px;
  color: ${theme.text.neutral.primary};
`;

const CellIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

// Add styled components for the docs link
const DocsLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 16px;
`;

const DocsLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${theme.fill.neutral.primary};
  border: 1px solid ${theme.border.brand.primary};
  border-radius: 4px;
  text-decoration: none;
  color: ${theme.text.brand.primary};
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.fill.brand.secondary};
  }
`;

const DocsLinkText = styled.span`
  margin-right: 4px;
`;

// Add the WebhookPageWrapper component before the StepApiKeys component
const WebhookPageWrapper = styled.div`
  width: 100%;
  overflow-y: visible;
  height: auto;
`;

// API Logs tab styles
const MethodBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background-color: ${(props) => `${props.color}20`};
  color: ${(props) => props.color};
  font-weight: 600;
  font-size: 12px;
  border-radius: 4px;
  min-width: 60px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid ${(props) => `${props.color}30`};
`;

// Add a StatusBadge styled component
const StatusBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background-color: ${(props) => `${props.color}20`};
  color: ${(props) => props.color};
  font-weight: 600;
  font-size: 12px;
  border-radius: 4px;
  min-width: 50px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid ${(props) => `${props.color}30`};
`;

// Modify the StatusCodeCellRenderer to use the new StatusBadge with correct theme color constants
const StatusCodeCellRenderer = (props: any) => {
  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) {
      return theme.SUCCESS500;
    } else if (code >= 400) {
      return theme.DANGER600;
    } else {
      return theme.NEUTRAL600;
    }
  };

  return <StatusBadge color={getStatusColor(props.value)}>{props.value}</StatusBadge>;
};

export const StepApiKeys: React.FC = () => {
  const { apiKeys } = useFlags();

  const gridRef = useRef<any>();
  const logsGridRef = useRef<any>();
  const webhookLogsGridRef = useRef<any>();
  const [activeTab, setActiveTab] = useState<"keys" | "logs" | "webhooks">("keys");
  const [perPage, setPerPage] = useState(25);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [apiKeyType, setApiKeyType] = useState<"READONLY" | "READWRITE">("READONLY");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showLogDetailsModal, setShowLogDetailsModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [logsPage, setLogsPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [selectedEvent, setSelectedEvent] = useState<WebHookEventType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [webhookLogsPage, setWebhookLogsPage] = useState(1);
  const [webhookLogsPerPage, setWebhookLogsPerPage] = useState(25);
  const [webhookLogsTotalCount, setWebhookLogsTotalCount] = useState(0);
  const [showWebhookLogDetailsModal, setShowWebhookLogDetailsModal] = useState(false);
  const [selectedWebhookLog, setSelectedWebhookLog] = useState<any>(null);

  // Format API key type to be more user-friendly
  const formatApiKeyType = (type: string) => {
    switch (type) {
      case "READONLY":
        return "Read Only";
      case "READWRITE":
        return "Read/Write";
      default:
        return type;
    }
  };

  // Query for API keys
  const { data: apiKeysData, loading: apiKeysLoading, refetch: refetchApiKeys } = useQuery(FETCH_API_TOKENS, {
    variables: {
      take: perPage,
      skip: 0,
    },
    fetchPolicy: "network-only",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
      errorToast("Error fetching API keys");
    },
  });

  // Query for API logs count
  const { data: apiLogsCountData, refetch: refetchApiLogsCount } = useQuery(FETCH_API_REQUEST_LOG_COUNT, {
    fetchPolicy: "network-only",
    skip: activeTab !== "logs",
    onCompleted(data) {
      if (data?.publicApiRequestLogCount !== undefined) {
        setLogsTotalCount(data.publicApiRequestLogCount);
      }
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  // Query for API logs
  const { data: apiLogsData, loading: apiLogsLoading, refetch: refetchApiLogs } = useQuery(FETCH_API_REQUEST_LOGS, {
    variables: {
      take: logsPerPage,
      skip: (logsPage - 1) * logsPerPage,
      orderBy: "desc",
      start_date: startDate ? new Date(startDate) : undefined,
      end_date: endDate ? new Date(endDate) : undefined,
    },
    fetchPolicy: "network-only",
    skip: activeTab !== "logs",
    onCompleted(data) {
      console.log("API Logs Data Received:", data?.publicApiRequestLogs?.length || 0, "records"); // Debug logging
      console.log("Filter conditions:", { startDate, endDate, logsPage, logsPerPage }); // Debug logging
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
      errorToast("Error fetching API logs");
    },
  });

  // Effect to update grid when API keys data changes
  useEffect(() => {
    if (gridRef.current?.api && apiKeysData?.fetchPublicApiTokens) {
      gridRef.current.api.setRowData(apiKeysData.fetchPublicApiTokens);
    }
  }, [apiKeysData]);

  // Effect to update logs grid when API logs data changes
  useEffect(() => {
    console.log("Logs useEffect triggered:", {
      hasGridApi: !!logsGridRef.current?.api,
      hasData: !!apiLogsData?.publicApiRequestLogs,
      dataLength: apiLogsData?.publicApiRequestLogs?.length,
      isLoading: apiLogsLoading,
    }); // Debug logging

    // Only handle overlay hiding, not data setting (AG Grid handles data via rowData prop)
    if (logsGridRef.current?.api && !apiLogsLoading) {
      try {
        console.log("Hiding overlay - loading complete"); // Debug logging
        logsGridRef.current.api.hideOverlay();
      } catch (e) {
        console.error("Error hiding overlay:", e);
      }
    }
  }, [apiLogsData, apiLogsLoading]);

  // TODO: Add webhook logs useEffect after line 485 (after webhook query)

  // Mutation for deleting API keys
  const [deleteApiToken, { loading: deleteLoading }] = useMutation(DELETE_API_TOKEN, {
    onCompleted({ deletePublicApiToken }) {
      if (deletePublicApiToken) {
        appToast("API key deleted successfully");
        refetchApiKeys();
        setShowDeleteModal(false);
        setKeyToDelete(null);
      }
    },
    onError({ message }) {
      console.error("Error deleting API key:", message);
      appToast("Error deleting API key");
    },
  });

  // Mutation for creating API keys
  const [createApiToken, { loading: createLoading }] = useMutation(CREATE_API_TOKEN, {
    onCompleted({ createPublicApiToken }) {
      if (createPublicApiToken) {
        appToast("API key created successfully");
        refetchApiKeys();
        setShowCreateModal(false);
        setNewKeyName("");
        setApiKeyType("READONLY"); // Reset key type to default
      }
    },
    onError({ message }) {
      console.error("Error creating API key:", message);
      appToast("Error creating API key");
    },
  });

  // Webhook queries and mutations
  const { data: configData, loading: configLoading, refetch: refetchConfig } = useQuery(FETCH_WEBHOOK_CONFIG, {
    fetchPolicy: "network-only",
    skip: activeTab !== "webhooks",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
      appToast("Error fetching webhook configuration");
    },
    onCompleted({ fetchWebhookConfig }) {
      if (fetchWebhookConfig) {
        setWebhookUrl(fetchWebhookConfig.webhook_url || "");
        setWebhookSecret(fetchWebhookConfig.webhook_secret || "");
        setIsEditing(!fetchWebhookConfig.webhook_url);
      } else {
        // No webhook configuration exists, allow editing
        setIsEditing(true);
      }
    },
  });

  const { data: subscriptionsData, loading: subscriptionsLoading, refetch: refetchSubscriptions } = useQuery(
    FETCH_WEBHOOK_EVENT_SUBSCRIPTIONS,
    {
      fetchPolicy: "network-only",
      skip: activeTab !== "webhooks",
      onError({ message, name }) {
        console.log(`Error in ${name}: `, message);
        appToast("Error fetching event subscriptions");
      },
    },
  );

  // Query for webhook logs
  const { data: webhookLogsData, loading: webhookLogsLoading, refetch: refetchWebhookLogs } = useQuery(
    FETCH_WEBHOOK_LOGS,
    {
      variables: {
        take: webhookLogsPerPage,
        skip: (webhookLogsPage - 1) * webhookLogsPerPage,
        order_by: "desc",
      },
      fetchPolicy: "network-only",
      skip: activeTab !== "webhooks",
      onError({ message, name }) {
        console.log(`Error in ${name}: `, message);
        errorToast("Error fetching webhook logs");
      },
    },
  );

  // Query for webhook logs count
  const { data: webhookLogsCountData, refetch: refetchWebhookLogsCount } = useQuery(FETCH_WEBHOOK_LOGS_COUNT, {
    fetchPolicy: "network-only",
    skip: activeTab !== "webhooks",
    onCompleted(data) {
      if (data?.fetchWebhookLogsCount !== undefined) {
        setWebhookLogsTotalCount(data.fetchWebhookLogsCount);
      }
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  // Mutation for webhook configuration
  const [createOrUpdateWebhookConfig, { loading: saveLoading }] = useMutation(CREATE_OR_UPDATE_WEBHOOK_CONFIG, {
    onCompleted() {
      appToast("Webhook configuration saved successfully");
      setIsEditing(false);
      refetchConfig();
    },
    onError({ message }) {
      console.error("Error saving webhook configuration:", message);
      errorToast("Error saving webhook configuration");
    },
  });

  // Mutation for webhook subscriptions
  const [createOrUpdateSubscription, { loading: subscribeLoading }] = useMutation(
    CREATE_OR_UPDATE_WEBHOOK_EVENT_SUBSCRIPTION,
    {
      onCompleted() {
        appToast("Event subscription saved successfully");
        setShowAddModal(false);
        setSelectedEvent(null);
        refetchSubscriptions();
      },
      onError({ message }) {
        console.error("Error subscribing to event:", message);
        appToast("Error subscribing to event");
      },
    },
  );

  // Add the mutation for unsubscribing from webhook events
  const [deleteWebhookEventSubscription, { loading: deleteSubscriptionLoading }] = useMutation(
    DELETE_WEBHOOK_EVENT_SUBSCRIPTION,
    {
      onCompleted() {
        appToast("Event unsubscribed successfully");
        refetchSubscriptions();
      },
      onError({ message }) {
        console.error("Error unsubscribing from event:", message);
        appToast("Error unsubscribing from event");
      },
    },
  );

  // Add ExportPublicApiRequestLogs mutation
  const [exportLogs, { loading: exportLogsLoading }] = useMutation(EXPORT_PUBLIC_API_REQUEST_LOGS, {
    onCompleted({ exportPublicApiRequestLogs }) {
      if (exportPublicApiRequestLogs) {
        appToast("Logs sent to your email successfully");
      }
      setIsExporting(false);
    },
    onError({ message }) {
      console.error("Error exporting logs:", message);
      appToast("Error exporting logs");
      setIsExporting(false);
    },
  });

  // Update refetch function to refetch both logs and count
  const refetchLogsAndCount = () => {
    refetchApiLogs();
    refetchApiLogsCount();
  };

  // Update refetch function for webhook logs to refetch both logs and count
  const refetchWebhookLogsAndCount = () => {
    refetchWebhookLogs();
    refetchWebhookLogsCount();
  };

  const handleTabChange = (tab: "keys" | "logs" | "webhooks") => {
    setActiveTab(tab);
    if (tab === "logs") {
      refetchLogsAndCount();
    } else if (tab === "webhooks") {
      refetchWebhookLogsAndCount();
    }
  };

  const handleCreateApiKey = () => {
    setShowCreateModal(true);
  };

  const handleSubmitCreateKey = () => {
    if (!newKeyName.trim()) {
      appToast("Please enter a name for the API key");
      return;
    }

    createApiToken({
      variables: {
        api_key_type: apiKeyType,
        name: newKeyName.trim(),
      },
    });
  };

  const handleCopyApiKey = (token: string) => {
    navigator.clipboard
      .writeText(token)
      .then(() => appToast("API key copied to clipboard"))
      .catch((err) => console.error("Could not copy API key: ", err));
  };

  const handleDeleteApiKey = (keyId: string, keyName: string) => {
    setKeyToDelete({ id: keyId, name: keyName });
    setShowDeleteModal(true);
  };

  const confirmDeleteApiKey = () => {
    if (keyToDelete) {
      deleteApiToken({
        variables: { id: keyToDelete.id },
      });
    }
  };

  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    refetchApiKeys();
  };

  const handleLogsPerPageChange = (value: number) => {
    // Show loading overlay for better UX
    if (logsGridRef.current?.api) {
      try {
        logsGridRef.current.api.showLoadingOverlay();
      } catch (e) {
        console.error("Error showing loading overlay:", e);
      }
    }

    setLogsPerPage(value);
    setLogsPage(1);

    // Refetch immediately with new page size
    refetchApiLogs({
      skip: 0, // First page
      take: value,
      orderBy: "desc",
      start_date: startDate ? new Date(startDate) : undefined,
      end_date: endDate ? new Date(endDate) : undefined,
    });
  };

  const handleViewLogDetails = (log: any) => {
    setSelectedLog(log);
    setShowLogDetailsModal(true);
  };

  const handleViewWebhookLogDetails = (log: any) => {
    setSelectedWebhookLog(log);
    setShowWebhookLogDetailsModal(true);
  };

  const handleDateChange = (dates: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    // Pass the moment objects directly in the query
    setStartDate(dates.startDate ? dates.startDate.startOf("day").format() : "");
    setEndDate(dates.endDate ? dates.endDate.endOf("day").format() : "");
  };

  const applyDateFilter = () => {
    console.log("Applying date filter:", { startDate, endDate, logsPerPage, activeTab }); // Debug logging

    // Show loading overlay immediately for better UX
    if (logsGridRef.current?.api) {
      try {
        logsGridRef.current.api.showLoadingOverlay();
      } catch (e) {
        console.error("Error showing loading overlay:", e);
      }
    }

    // Reset to first page - this will trigger the query to refetch due to variable changes
    setLogsPage(1);

    // Force a manual refetch after a short delay to ensure state has updated
    setTimeout(() => {
      const variables = {
        skip: 0,
        take: logsPerPage,
        orderBy: "desc",
        start_date: startDate ? new Date(startDate) : undefined,
        end_date: endDate ? new Date(endDate) : undefined,
      };

      console.log("Force refetching with variables:", variables);

      refetchApiLogs(variables)
        .then((result) => {
          console.log("Manual refetch completed:", result.data?.publicApiRequestLogs?.length, "records");
          // Hide the loading overlay directly here
          if (logsGridRef.current?.api) {
            try {
              logsGridRef.current.api.hideOverlay();
              // Try multiple times to ensure it's hidden
              setTimeout(() => {
                if (logsGridRef.current?.api) {
                  logsGridRef.current.api.hideOverlay();
                }
              }, 100);
              console.log("Loading overlay hidden after refetch"); // Debug logging
            } catch (e) {
              console.error("Error hiding loading overlay:", e);
            }
          }
        })
        .catch((error) => {
          console.error("Manual refetch failed:", error);
          // Hide overlay even on error
          if (logsGridRef.current?.api) {
            try {
              logsGridRef.current.api.hideOverlay();
            } catch (e) {
              console.error("Error hiding loading overlay on error:", e);
            }
          }
        });
    }, 50);
  };

  // Handle export logs
  const handleExportLogs = () => {
    if (!startDate || !endDate) {
      appToast("Please select a date range to export logs");
      return;
    }

    setIsExporting(true);
    exportLogs({
      variables: {
        start_date: new Date(startDate),
        end_date: new Date(endDate),
        order_by: "desc",
      },
    });
  };

  const handlePrevPage = () => {
    if (logsPage > 1) {
      const prevPage = logsPage - 1;

      // First update page state
      setLogsPage(prevPage);

      // Show loading overlay for better UX
      if (logsGridRef.current?.api) {
        try {
          logsGridRef.current.api.showLoadingOverlay();
        } catch (e) {
          console.error("Error showing loading overlay:", e);
        }
      }

      // Fetch new data immediately
      refetchApiLogs({
        skip: (prevPage - 1) * logsPerPage,
        take: logsPerPage,
        orderBy: "desc",
        start_date: startDate ? new Date(startDate) : undefined,
        end_date: endDate ? new Date(endDate) : undefined,
      });
    }
  };

  const handleNextPage = () => {
    if (!apiLogsLoading && apiLogsData?.publicApiRequestLogs?.length === logsPerPage) {
      const nextPage = logsPage + 1;

      // First update page state
      setLogsPage(nextPage);

      // Show loading overlay for better UX
      if (logsGridRef.current?.api) {
        try {
          logsGridRef.current.api.showLoadingOverlay();
        } catch (e) {
          console.error("Error showing loading overlay:", e);
        }
      }

      // Fetch new data immediately
      refetchApiLogs({
        skip: (nextPage - 1) * logsPerPage,
        take: logsPerPage,
        orderBy: "desc",
        start_date: startDate ? new Date(startDate) : undefined,
        end_date: endDate ? new Date(endDate) : undefined,
      });
    }
  };

  const getMaxPage = () => {
    return Math.max(1, Math.ceil(logsTotalCount / logsPerPage));
  };

  const getWebhookLogsMaxPage = () => {
    return Math.max(1, Math.ceil(webhookLogsTotalCount / webhookLogsPerPage));
  };

  // Webhook logs pagination handlers
  const handleWebhookLogsPrevPage = () => {
    if (webhookLogsPage > 1) {
      const prevPage = webhookLogsPage - 1;
      setWebhookLogsPage(prevPage);

      if (webhookLogsGridRef.current?.api) {
        try {
          webhookLogsGridRef.current.api.showLoadingOverlay();
          webhookLogsGridRef.current.api.setRowData([]);
        } catch (e) {
          console.error("Error clearing webhook logs grid data:", e);
        }
      }

      setTimeout(() => {
        refetchWebhookLogs({
          skip: (prevPage - 1) * webhookLogsPerPage,
          take: webhookLogsPerPage,
          order_by: "desc",
        });
      }, 50);
    }
  };

  const handleWebhookLogsNextPage = () => {
    if (!webhookLogsLoading && webhookLogsData?.fetchWebhookLogs?.length === webhookLogsPerPage) {
      const nextPage = webhookLogsPage + 1;
      setWebhookLogsPage(nextPage);

      if (webhookLogsGridRef.current?.api) {
        try {
          webhookLogsGridRef.current.api.showLoadingOverlay();
          webhookLogsGridRef.current.api.setRowData([]);
        } catch (e) {
          console.error("Error clearing webhook logs grid data:", e);
        }
      }

      setTimeout(() => {
        refetchWebhookLogs({
          skip: (nextPage - 1) * webhookLogsPerPage,
          take: webhookLogsPerPage,
          order_by: "desc",
        });
      }, 50);
    }
  };

  const handleWebhookLogsPerPageChange = (value: number) => {
    if (webhookLogsGridRef.current?.api) {
      try {
        webhookLogsGridRef.current.api.showLoadingOverlay();
        webhookLogsGridRef.current.api.setRowData([]);
      } catch (e) {
        console.error("Error clearing webhook logs grid data:", e);
      }
    }

    setWebhookLogsPerPage(value);
    setWebhookLogsPage(1);

    setTimeout(() => {
      refetchWebhookLogs({
        skip: 0,
        take: value,
        order_by: "desc",
      });
    }, 50);
  };

  // AG Grid Cell Renderers
  const ApiKeyCellRenderer = (props: any) => {
    return (
      <ApiKeyContainer>
        <ApiKeyText>{props.value ? `${props.value.substring(0, 15)}...` : "••••••••••••••••"}</ApiKeyText>
        <PhoenixIcon
          svg={copy}
          size={16}
          color={theme.icon.brand.default}
          hoverColor={theme.icon.brand.default}
          alt="copy"
          pointer
          onClick={() => handleCopyApiKey(props.value)}
        />
      </ApiKeyContainer>
    );
  };

  const DeleteCellRenderer = (props: any) => {
    return (
      <CellIconContainer>
        <PhoenixIcon
          svg={trash}
          size={16}
          color={theme.icon.danger.default}
          hoverColor={theme.icon.danger.hover}
          alt="delete"
          pointer
          onClick={() => handleDeleteApiKey(props.data.id, props.data.name)}
        />
      </CellIconContainer>
    );
  };

  const DateCellRenderer = (props: any) => {
    return props.value ? moment(props.value).format("MM/DD/YYYY") : "Never";
  };

  // AG Grid Cell Renderers for Logs
  const DateTimeCellRenderer = (props: any) => {
    return props.value ? moment(props.value).format("MM/DD/YYYY HH:mm:ss") : "N/A";
  };

  const MethodCellRenderer = (props: any) => {
    const getMethodColor = (method: string) => {
      switch (method) {
        case "GET":
          return theme.PRIMARY600;
        case "POST":
          return theme.SUCCESS700;
        case "PUT":
          return theme.WARNING600;
        case "PATCH":
          return theme.PRIMARY700;
        case "DELETE":
          return theme.DANGER700;
        default:
          return theme.NEUTRAL600;
      }
    };

    return <MethodBadge color={getMethodColor(props.value)}>{props.value}</MethodBadge>;
  };

  // Add the ViewLogCellRenderer back
  const ViewLogCellRenderer = (props: any) => {
    return (
      <CellIconContainer>
        <PhoenixIcon
          svg={eye}
          size={16}
          color={theme.icon.brand.default}
          hoverColor={theme.icon.brand.hover}
          alt="view"
          pointer
          onClick={() => handleViewLogDetails(props.data)}
        />
      </CellIconContainer>
    );
  };

  const ViewWebhookLogCellRenderer = (props: any) => {
    return (
      <CellIconContainer>
        <PhoenixIcon
          svg={eye}
          size={16}
          color={theme.icon.brand.default}
          hoverColor={theme.icon.brand.hover}
          alt="view"
          pointer
          onClick={() => handleViewWebhookLogDetails(props.data)}
        />
      </CellIconContainer>
    );
  };

  // AG Grid Column Definitions
  const columnDefs = [
    {
      headerName: "Name",
      field: "name",
      flex: 1,
      minWidth: 180,
      headerClass: "ag-api-keys-header",
    },
    {
      headerName: "API Key",
      field: "token",
      flex: 2,
      minWidth: 300,
      headerClass: "ag-api-keys-header",
      cellRenderer: ApiKeyCellRenderer,
    },
    {
      headerName: "Type",
      field: "type",
      flex: 1,
      minWidth: 140,
      headerClass: "ag-api-keys-header",
      valueFormatter: (params: any) => formatApiKeyType(params.value),
    },
    {
      headerName: "Date Created",
      field: "created_at",
      flex: 1,
      minWidth: 140,
      headerClass: "ag-api-keys-header",
      cellRenderer: DateCellRenderer,
    },
    {
      headerName: "Last Used",
      field: "last_used_at",
      flex: 1,
      minWidth: 140,
      headerClass: "ag-api-keys-header",
      cellRenderer: DateCellRenderer,
    },
    {
      headerName: "",
      field: "id",
      width: 70,
      headerClass: "ag-api-keys-header",
      cellRenderer: DeleteCellRenderer,
    },
  ];

  // AG Grid Column Definitions for Logs
  const logsColumnDefs = [
    {
      headerName: "Date and Time",
      field: "created_at",
      flex: 1.5,
      minWidth: 180,
      headerClass: "ag-api-keys-header",
      cellRenderer: DateTimeCellRenderer,
    },
    {
      headerName: "Status Code",
      field: "response_code",
      flex: 0.7,
      minWidth: 120,
      maxWidth: 140,
      headerClass: "ag-api-keys-header",
      cellRenderer: StatusCodeCellRenderer,
      cellStyle: () => ({ textAlign: "left", display: "flex", alignItems: "center" }),
    },
    {
      headerName: "Request ID",
      field: "id",
      flex: 1.5,
      minWidth: 150,
      headerClass: "ag-api-keys-header",
      cellRenderer: (params: any) => {
        // Display truncated ID with tooltip
        const id = params.value;
        return <span>{id && id.length > 15 ? id.substring(0, 15) + "..." : id}</span>;
      },
    },
    {
      headerName: "Request Method",
      field: "method",
      flex: 0.8,
      minWidth: 140,
      maxWidth: 160,
      headerClass: "ag-api-keys-header",
      cellRenderer: MethodCellRenderer,
    },
    {
      headerName: "Request URL",
      field: "endpoint",
      flex: 2,
      minWidth: 200,
      headerClass: "ag-api-keys-header",
      cellRenderer: (params: any) => {
        // Return just the plain string - AG Grid will handle the truncation
        return <span>{params.value}</span>;
      },
    },
    {
      headerName: "Response Time",
      field: "execution_time",
      flex: 0.8,
      minWidth: 120,
      maxWidth: 150,
      headerClass: "ag-api-keys-header",
      valueFormatter: (params: any) => `${params.value} ms`,
      cellStyle: () => ({ textAlign: "left", display: "flex", alignItems: "center" }),
    },
    {
      headerName: "",
      field: "id",
      width: 70,
      headerClass: "ag-api-keys-header",
      cellRenderer: ViewLogCellRenderer,
    },
  ];

  // AG Grid Column Definitions for Webhook Logs
  const webhookLogsColumnDefs = [
    {
      headerName: "Date and Time",
      field: "created_at",
      flex: 1.5,
      minWidth: 180,
      headerClass: "ag-api-keys-header",
      cellRenderer: DateTimeCellRenderer,
    },
    {
      headerName: "Status Code",
      field: "response_code",
      flex: 0.7,
      minWidth: 120,
      maxWidth: 140,
      headerClass: "ag-api-keys-header",
      cellRenderer: StatusCodeCellRenderer,
      cellStyle: () => ({ textAlign: "left", display: "flex", alignItems: "center" }),
    },
    {
      headerName: "Log ID",
      field: "id",
      flex: 1.5,
      minWidth: 150,
      headerClass: "ag-api-keys-header",
      cellRenderer: (params: any) => {
        const id = params.value;
        return <span>{id && id.length > 15 ? id.substring(0, 15) + "..." : id}</span>;
      },
    },
    {
      headerName: "Method",
      field: "method",
      flex: 0.8,
      minWidth: 140,
      maxWidth: 160,
      headerClass: "ag-api-keys-header",
      cellRenderer: MethodCellRenderer,
    },
    {
      headerName: "Webhook URL",
      field: "url",
      flex: 2,
      minWidth: 200,
      headerClass: "ag-api-keys-header",
      cellRenderer: (params: any) => {
        return <span>{params.value}</span>;
      },
    },
    {
      headerName: "Events",
      field: "event_types",
      flex: 1.2,
      minWidth: 150,
      headerClass: "ag-api-keys-header",
      cellRenderer: (params: any) => {
        const eventTypes = params.value;
        if (Array.isArray(eventTypes) && eventTypes.length > 0) {
          const eventTypeDisplay: Record<WebHookEventType, string> = {
            [WebHookEventType.LeadUpdated]: "Lead Updated",
            // [WebHookEventType.LeadUpdatedSpecificSystemFields]: "Lead Updated Specific System Fields", // Commented out - not supported for now
            [WebHookEventType.LeadCreated]: "Lead Created",
            [WebHookEventType.LeadActivityCreated]: "Lead Activity Created",
            [WebHookEventType.LeadActivityDemoSetCreated]: "Demo Set",
            [WebHookEventType.LeadActivityDemoReescheduled]: "Demo Rescheduled",
            [WebHookEventType.LeadActivityNewCallNote]: "New Call Note",
            [WebHookEventType.LeadActivityNewSMS]: "New SMS",
            [WebHookEventType.LeadActivityNewEmail]: "New Email",
          };

          const displayNames = eventTypes.map((eventType: string) => {
            return eventTypeDisplay[eventType as WebHookEventType] || eventType;
          });

          return <span>{displayNames.join(", ")}</span>;
        }
        return <span>-</span>;
      },
    },
    {
      headerName: "",
      field: "id",
      width: 70,
      headerClass: "ag-api-keys-header",
      cellRenderer: ViewWebhookLogCellRenderer,
    },
  ];

  const renderApiKeysTab = () => {
    return (
      <TabContent>
        <TabContentWrapper>
          <KeysWarningMessage>
            <KeysWarningText>
              Please treat your API key with care. It is a unique identifier crucial to the security of your account and
              should not be shared or displayed in areas accessible to others, such as client-side code or repositories.
              If an API key is exposed, it may be subject to automatic revocation to prevent unauthorized access to your
              account.
            </KeysWarningText>
          </KeysWarningMessage>

          <TableContainer>
            <div className="ag-theme-alpine" style={{ width: "100%", height: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={apiKeysData?.fetchPublicApiTokens || []}
                columnDefs={columnDefs}
                suppressMovableColumns={true}
                suppressDragLeaveHidesColumns={true}
                tooltipMouseTrack={true}
                enableCellTextSelection={true}
                loadingOverlayComponent={() => <LoadingContainer>Loading API keys...</LoadingContainer>}
                noRowsOverlayComponent={() => <EmptyContainer>No API keys found</EmptyContainer>}
                onGridSizeChanged={(params: any) => {
                  params.api.sizeColumnsToFit();
                }}
                immutableData={true}
                getRowId={(params) => params.data.id}
                onFirstDataRendered={(params) => {
                  params.api.sizeColumnsToFit();
                }}
                domLayout="autoHeight"
              />
            </div>
          </TableContainer>

          <TableFooter>
            <FlexDiv>
              <ItemsPerPageText>Items Per Page</ItemsPerPageText>
              {[25, 50, 75, 100].map((value) => (
                <PageSizeButton key={value} selected={perPage === value} onClick={() => handlePerPageChange(value)}>
                  {value}
                </PageSizeButton>
              ))}
            </FlexDiv>

            <ButtonContainer>
              <CreateButton
                variant="brand"
                buttonType="secondary"
                onClick={handleCreateApiKey}
                disabled={createLoading}
                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <PhoenixIcon
                  svg={copy}
                  size={16}
                  color={theme.icon.neutral.inverse}
                  alt="create"
                  style={{ marginRight: "8px" }}
                />
                Create New API Key
              </CreateButton>
            </ButtonContainer>
          </TableFooter>
        </TabContentWrapper>

        {/* Create API Key Modal */}
        {showCreateModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <AppText fontSize={18} fontWeight={500}>
                  Create New API Key
                </AppText>
                <PhoenixIcon
                  svg={xIcon}
                  size={16}
                  color={theme.icon.neutral.default}
                  hoverColor={theme.icon.neutral.hover}
                  alt="close"
                  pointer
                  onClick={() => setShowCreateModal(false)}
                />
              </ModalHeader>
              <ModalBody>
                <AppText fontSize={14} style={{ marginBottom: "12px" }}>
                  Enter a name for this API key. This name is for reference only and will not affect the key's
                  functionality.
                </AppText>
                <PhoenixInput
                  type="text"
                  placeholder="API Key Name"
                  value={newKeyName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKeyName(e.target.value)}
                  style={{ width: "100%", marginBottom: "24px" }}
                />

                <PermissionSectionTitle>API Permissions</PermissionSectionTitle>

                <PermissionOptions>
                  <PermissionOption onClick={() => setApiKeyType("READONLY")}>
                    <RadioInput type="radio" checked={apiKeyType === "READONLY"} readOnly />
                    <PermissionLabel>
                      <PermissionName>Read Only</PermissionName>
                    </PermissionLabel>
                  </PermissionOption>

                  <PermissionOption onClick={() => setApiKeyType("READWRITE")}>
                    <RadioInput type="radio" checked={apiKeyType === "READWRITE"} readOnly />
                    <PermissionLabel>
                      <PermissionName>Read/Write</PermissionName>
                    </PermissionLabel>
                  </PermissionOption>
                </PermissionOptions>

                <ModalActions>
                  <PhoenixAppButton
                    variant="brand-outline"
                    buttonType="secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </PhoenixAppButton>
                  <PhoenixAppButton
                    variant="brand"
                    buttonType="secondary"
                    onClick={handleSubmitCreateKey}
                    disabled={!newKeyName.trim() || createLoading}
                  >
                    Continue
                  </PhoenixAppButton>
                </ModalActions>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Delete Confirmation Modal */}
        <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} popupAnimation width={450}>
          <DeleteModalContent>
            <ModalHeader>
              <AppText fontSize={18} fontWeight={600}>
                Delete API Key
              </AppText>
            </ModalHeader>
            <DeleteModalBody>
              <AppText fontSize={14}>
                Are you sure you want to delete the API key "{keyToDelete?.name}"? This action cannot be undone.
              </AppText>
            </DeleteModalBody>
            <DeleteModalFooter>
              <PhoenixAppButton
                buttonType="secondary"
                variant="danger-outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </PhoenixAppButton>
              <PhoenixAppButton
                buttonType="secondary"
                variant="brand"
                onClick={confirmDeleteApiKey}
                disabled={deleteLoading}
              >
                Delete
              </PhoenixAppButton>
            </DeleteModalFooter>
          </DeleteModalContent>
        </Modal>
      </TabContent>
    );
  };

  const renderApiLogsTab = () => {
    return (
      <TabContent>
        <TabContentWrapper>
          <LogsExplanationContainer>
            <ExplanationTitle>API Request Logs</ExplanationTitle>
            <ExplanationText>
              View and export logs of API requests made to your organization's endpoints. Use the date range filter to
              narrow down results.
            </ExplanationText>
          </LogsExplanationContainer>

          <DateFilterContainer>
            <DateFilterWrapper>
              <DatePickerContainer>
                <FilterLabel>Date Range</FilterLabel>
                <PhoenixDateRange
                  lowerbound_date={startDate ? moment(startDate) : null}
                  upperbound_date={endDate ? moment(endDate) : null}
                  onDateChange={handleDateChange}
                  minWidth={240}
                  style={{ marginLeft: 85 }}
                />
              </DatePickerContainer>
              <ActionsContainer>
                <FilterButton
                  variant="brand"
                  buttonType="secondary"
                  onClick={applyDateFilter}
                  disabled={!startDate && !endDate}
                >
                  Apply Filter
                </FilterButton>
                <ExportButton
                  variant="brand-outline"
                  buttonType="secondary"
                  onClick={handleExportLogs}
                  disabled={!startDate || !endDate || isExporting || exportLogsLoading}
                >
                  <PhoenixIcon
                    svg={download}
                    size={16}
                    color={theme.icon.brand.default}
                    alt="export"
                    style={{ marginRight: "8px" }}
                  />
                  Export Logs
                </ExportButton>
              </ActionsContainer>
            </DateFilterWrapper>
          </DateFilterContainer>

          <TableContainer>
            <div className="ag-theme-alpine" style={{ width: "100%", height: "50vh" }}>
              <AgGridReact
                ref={logsGridRef}
                rowData={apiLogsData?.publicApiRequestLogs || []}
                columnDefs={logsColumnDefs}
                suppressMovableColumns={true}
                suppressDragLeaveHidesColumns={true}
                tooltipShowDelay={0}
                tooltipMouseTrack={true}
                enableCellTextSelection={true}
                loadingOverlayComponent={() => <LoadingContainer>Loading API logs...</LoadingContainer>}
                noRowsOverlayComponent={() => <EmptyContainer>No API logs found</EmptyContainer>}
                onGridSizeChanged={(params: any) => {
                  if (params.api) {
                    params.api.sizeColumnsToFit();
                  }
                }}
                onGridReady={(params: any) => {
                  console.log("Grid ready, hiding overlay"); // Debug logging
                  if (params.api && !apiLogsLoading) {
                    params.api.hideOverlay();
                  }
                }}
                onRowDataUpdated={(params: any) => {
                  console.log("Row data updated, hiding overlay"); // Debug logging
                  if (params.api) {
                    params.api.hideOverlay();
                  }
                }}
                getRowId={(params) => params.data.id}
                immutableData={true}
                domLayout="normal"
                onFirstDataRendered={(params) => {
                  if (params.api) {
                    params.api.sizeColumnsToFit();
                  }
                }}
              />
            </div>
          </TableContainer>

          <TableFooter>
            <FlexDiv>
              <ItemsPerPageText>Items Per Page</ItemsPerPageText>
              {[25, 50, 75, 100].map((value) => (
                <PageSizeButton
                  key={value}
                  selected={logsPerPage === value}
                  onClick={() => handleLogsPerPageChange(value)}
                >
                  {value}
                </PageSizeButton>
              ))}
            </FlexDiv>

            <FlexDiv align="center" gap={8}>
              <AppText fontSize={12} fontWeight={400} style={{ marginRight: "8px" }}>
                Page {logsPage} of {getMaxPage()}
              </AppText>
              <PhoenixIcon
                svg={arrow_left}
                size={20}
                variant={logsPage === 1 || apiLogsLoading ? "neutral" : "brand"}
                pointer={logsPage !== 1 && !apiLogsLoading}
                onClick={handlePrevPage}
                style={{ margin: "0 4px" }}
              />
              <PhoenixIcon
                svg={arrow_right}
                size={20}
                variant={
                  apiLogsLoading || apiLogsData?.publicApiRequestLogs?.length < logsPerPage ? "neutral" : "brand"
                }
                pointer={!apiLogsLoading && apiLogsData?.publicApiRequestLogs?.length === logsPerPage}
                onClick={handleNextPage}
                style={{ margin: "0 4px" }}
              />
            </FlexDiv>
          </TableFooter>

          {/* Add bottom spacer to push content away from bottom of window */}
          <BottomSpacer />
        </TabContentWrapper>

        {/* Log Details Modal */}
        {showLogDetailsModal && selectedLog && (
          <Modal open={showLogDetailsModal} onClose={() => setShowLogDetailsModal(false)} popupAnimation width={700}>
            <LogDetailsContent>
              <ModalHeader>
                <AppText fontSize={18} fontWeight={600}>
                  API Request Details
                </AppText>
              </ModalHeader>
              <LogDetailsBody>
                <LogDetailSection>
                  <LogDetailLabel>Request ID</LogDetailLabel>
                  <LogDetailValue>{selectedLog.id}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Date and Time</LogDetailLabel>
                  <LogDetailValue>{moment(selectedLog.created_at).format("MM/DD/YYYY HH:mm:ss")}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Method</LogDetailLabel>
                  <MethodBadge
                    color={
                      selectedLog.method === "GET"
                        ? theme.PRIMARY600
                        : selectedLog.method === "POST"
                        ? theme.SUCCESS700
                        : selectedLog.method === "PUT"
                        ? theme.WARNING600
                        : selectedLog.method === "PATCH"
                        ? theme.PRIMARY700
                        : selectedLog.method === "DELETE"
                        ? theme.DANGER700
                        : theme.NEUTRAL600
                    }
                  >
                    {selectedLog.method}
                  </MethodBadge>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>URL</LogDetailLabel>
                  <LogDetailValue>{selectedLog.endpoint}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Status Code</LogDetailLabel>
                  <LogDetailValue>{selectedLog.response_code}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Execution Time</LogDetailLabel>
                  <LogDetailValue>{selectedLog.execution_time} ms</LogDetailValue>
                </LogDetailSection>
                <LogDetailBodySection
                  requestBody={selectedLog.request_body_safe}
                  responseBody={selectedLog.response_body_safe}
                  body={selectedLog.body}
                />
              </LogDetailsBody>
              <ModalActions>
                <PhoenixAppButton
                  variant="brand"
                  buttonType="secondary"
                  onClick={() => setShowLogDetailsModal(false)}
                  style={{ minWidth: "120px" }}
                >
                  Close
                </PhoenixAppButton>
              </ModalActions>
            </LogDetailsContent>
          </Modal>
        )}
      </TabContent>
    );
  };

  const renderWebhooksTab = () => {
    const webhookConfig = configData?.fetchWebhookConfig;
    const webhookEnabled = !!webhookConfig && !!webhookConfig.webhook_url;

    // Now we expect an array from the backend
    const subscriptions = subscriptionsData?.fetchWebhookEventSubscriptions || [];

    const handleSaveConfig = () => {
      if (!webhookUrl.trim()) {
        appToast("Please enter a webhook URL");
        return;
      }

      createOrUpdateWebhookConfig({
        variables: {
          webhook_url: webhookUrl.trim(),
          webhook_secret: webhookSecret.trim(),
        },
      });
    };

    const handleEdit = () => {
      setIsEditing(true);
    };

    const handleCancel = () => {
      setWebhookUrl(webhookConfig?.webhook_url || "");
      setWebhookSecret(webhookConfig?.webhook_secret || "");
      setIsEditing(false);
    };

    const handleSubscribe = () => {
      if (!selectedEvent) {
        appToast("Please select an event type");
        return;
      }

      createOrUpdateSubscription({
        variables: {
          event_type: selectedEvent,
          fields: [],
        },
      });
    };

    const isEventSubscribed = (eventType: WebHookEventType) => {
      return subscriptions.some((sub: { event_type: string }) => sub.event_type === eventType) || false;
    };

    const eventTypeDisplay: Record<WebHookEventType, string> = {
      [WebHookEventType.LeadUpdated]: "Lead Updated",
      // [WebHookEventType.LeadUpdatedSpecificSystemFields]: "Lead Updated Specific System Fields", // Commented out - not supported for now
      [WebHookEventType.LeadCreated]: "Lead Created",
      [WebHookEventType.LeadActivityCreated]: "Lead Activity Created",
      [WebHookEventType.LeadActivityDemoSetCreated]: "Demo Set",
      [WebHookEventType.LeadActivityDemoReescheduled]: "Demo Rescheduled",
      [WebHookEventType.LeadActivityNewCallNote]: "New Call Note",
      [WebHookEventType.LeadActivityNewSMS]: "New SMS",
      [WebHookEventType.LeadActivityNewEmail]: "New Email",
    };

    const eventOptions = Object.values(WebHookEventType)
      .filter((event) => !isEventSubscribed(event))
      .map((event) => ({
        label: eventTypeDisplay[event],
        value: event,
      }));

    const handleUnsubscribe = (id: string) => {
      deleteWebhookEventSubscription({
        variables: { id },
      });
    };

    return (
      <TabContent>
        <TabContentWrapper>
          <WebhookPageWrapper>
            <WebhookTabContainer>
              {/* Webhook Config */}
              <WebhookFormContainer>
                <WebhookFormHeader>
                  <AppText fontSize={16} fontWeight={600}>
                    Webhook Configuration
                  </AppText>
                  {!isEditing && webhookConfig && webhookConfig.webhook_url && (
                    <PhoenixAppButton
                      variant="brand-outline"
                      buttonType="secondary"
                      onClick={handleEdit}
                      disabled={configLoading}
                    >
                      Edit Configuration
                    </PhoenixAppButton>
                  )}
                </WebhookFormHeader>

                <WebhookDescription>
                  Configure a webhook endpoint to receive real-time updates about events in your account. All webhook
                  requests will include a signature header that you can use to verify the authenticity of the requests.
                </WebhookDescription>

                <FormGroup>
                  <FormLabel>Webhook URL</FormLabel>
                  <PhoenixInput
                    type="text"
                    placeholder="https://example.com/webhook"
                    value={webhookUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebhookUrl(e.target.value)}
                    disabled={!isEditing && !!webhookConfig}
                    style={{ width: "100%" }}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Webhook Secret</FormLabel>
                  <PhoenixInput
                    type="text"
                    placeholder="Enter a secret key to secure your webhook"
                    value={webhookSecret}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebhookSecret(e.target.value)}
                    disabled={!isEditing && !!webhookConfig}
                    style={{ width: "100%" }}
                  />
                  <FormHelpText>
                    This secret will be used to create a signature for each request, allowing you to verify that the
                    request came from OpsIQ.
                  </FormHelpText>
                </FormGroup>

                {(isEditing || !webhookConfig || !webhookConfig.webhook_url) && (
                  <ButtonGroup>
                    {webhookConfig && webhookConfig.webhook_url && (
                      <PhoenixAppButton
                        variant="brand-outline"
                        buttonType="secondary"
                        onClick={handleCancel}
                        disabled={saveLoading}
                      >
                        Cancel
                      </PhoenixAppButton>
                    )}
                    <PhoenixAppButton
                      variant="brand"
                      buttonType="secondary"
                      onClick={handleSaveConfig}
                      disabled={saveLoading}
                    >
                      Save Configuration
                    </PhoenixAppButton>
                  </ButtonGroup>
                )}
              </WebhookFormContainer>

              {/* Event Subscriptions */}
              <WebhookSubscriptionsContainer>
                <WebhookFormHeader>
                  <AppText fontSize={16} fontWeight={600}>
                    Event Subscriptions
                  </AppText>
                  <PhoenixAppButton
                    variant="brand"
                    buttonType="secondary"
                    onClick={() => setShowAddModal(true)}
                    disabled={subscriptionsLoading || !webhookEnabled || eventOptions.length === 0}
                  >
                    Subscribe to Event
                  </PhoenixAppButton>
                </WebhookFormHeader>

                <WebhookDescription>
                  Subscribe to events to receive notifications when they occur in your account. You must configure a
                  webhook endpoint before subscribing to events.
                </WebhookDescription>

                {!webhookEnabled && (
                  <WebhookAlert>
                    <AppText fontSize={14} color={theme.text.warning.primary}>
                      Please configure a webhook endpoint before subscribing to events.
                    </AppText>
                  </WebhookAlert>
                )}

                {webhookEnabled && (!subscriptions || subscriptions.length === 0) && (
                  <EmptySubscriptions>
                    <AppText fontSize={14} color={theme.text.neutral.secondary}>
                      You haven't subscribed to any events yet. Click "Subscribe to Event" to get started.
                    </AppText>
                  </EmptySubscriptions>
                )}

                {webhookEnabled && subscriptions && subscriptions.length > 0 && (
                  <SubscriptionsList>
                    {subscriptions.map(
                      (subscription: { id: string; event_type: string; created_at: string | number }) => (
                        <SubscriptionItem key={subscription.id}>
                          <SubscriptionItemContent>
                            <EventTypeBadge>
                              {eventTypeDisplay[subscription.event_type as WebHookEventType] || subscription.event_type}
                            </EventTypeBadge>
                            <SubscriptionActions>
                              <SubscriptionDetails>
                                <AppText fontSize={12} color={theme.text.neutral.secondary}>
                                  Subscribed on {moment(subscription.created_at).format("MMM D, YYYY")}
                                </AppText>
                              </SubscriptionDetails>
                              <UnsubscribeButton onClick={() => handleUnsubscribe(subscription.id)}>
                                <PhoenixIcon
                                  svg={trash}
                                  size={14}
                                  color={theme.icon.danger.default}
                                  hoverColor={theme.icon.danger.hover}
                                  alt="unsubscribe"
                                  pointer
                                />
                                <UnsubscribeText>Unsubscribe</UnsubscribeText>
                              </UnsubscribeButton>
                            </SubscriptionActions>
                          </SubscriptionItemContent>
                        </SubscriptionItem>
                      ),
                    )}
                  </SubscriptionsList>
                )}

                {/* Add Event Subscription Modal */}
                {showAddModal && (
                  <Modal open={showAddModal} onClose={() => setShowAddModal(false)} popupAnimation width={500}>
                    <ModalContent>
                      <ModalHeader>
                        <AppText fontSize={18} fontWeight={600}>
                          Subscribe to Event
                        </AppText>
                      </ModalHeader>
                      <ModalBody>
                        <AppText fontSize={14} style={{ marginBottom: "16px" }}>
                          Select an event type to subscribe to. You will receive a webhook notification whenever this
                          event occurs.
                        </AppText>

                        <FormGroup>
                          <FormLabel>Event Type</FormLabel>
                          <WebhookEventSelector>
                            {eventOptions.map((option) => (
                              <EventOption
                                key={option.value}
                                selected={selectedEvent === option.value}
                                onClick={() => setSelectedEvent(option.value as WebHookEventType)}
                              >
                                {option.label}
                              </EventOption>
                            ))}
                          </WebhookEventSelector>
                        </FormGroup>

                        <ModalActions>
                          <PhoenixAppButton
                            variant="brand-outline"
                            buttonType="secondary"
                            onClick={() => setShowAddModal(false)}
                          >
                            Cancel
                          </PhoenixAppButton>
                          <PhoenixAppButton
                            variant="brand"
                            buttonType="secondary"
                            onClick={handleSubscribe}
                            disabled={!selectedEvent || subscribeLoading}
                          >
                            Subscribe
                          </PhoenixAppButton>
                        </ModalActions>
                      </ModalBody>
                    </ModalContent>
                  </Modal>
                )}
              </WebhookSubscriptionsContainer>

              {/* Webhook Logs Section */}
              <WebhookLogsContainer>
                <WebhookFormHeader>
                  <AppText fontSize={16} fontWeight={600}>
                    Webhook Logs
                  </AppText>
                </WebhookFormHeader>

                <WebhookDescription>
                  View recent webhook delivery logs to monitor webhook performance and troubleshoot any delivery issues.
                </WebhookDescription>

                <TableContainer>
                  <div className="ag-theme-alpine" style={{ width: "100%", height: "50vh" }}>
                    <AgGridReact
                      ref={webhookLogsGridRef}
                      rowData={webhookLogsData?.fetchWebhookLogs || []}
                      columnDefs={webhookLogsColumnDefs}
                      suppressMovableColumns={true}
                      suppressDragLeaveHidesColumns={true}
                      tooltipShowDelay={0}
                      tooltipMouseTrack={true}
                      enableCellTextSelection={true}
                      loadingOverlayComponent={() => <LoadingContainer>Loading webhook logs...</LoadingContainer>}
                      noRowsOverlayComponent={() => <EmptyContainer>No webhook logs found</EmptyContainer>}
                      onGridSizeChanged={(params: any) => {
                        if (params.api) {
                          params.api.sizeColumnsToFit();
                        }
                      }}
                      getRowId={(params) => params.data.id}
                      immutableData={true}
                      domLayout="normal"
                      onFirstDataRendered={(params) => {
                        if (params.api) {
                          params.api.sizeColumnsToFit();
                        }
                      }}
                    />
                  </div>
                </TableContainer>

                <TableFooter>
                  <FlexDiv>
                    <ItemsPerPageText>Items Per Page</ItemsPerPageText>
                    {[25, 50, 75, 100].map((value) => (
                      <PageSizeButton
                        key={value}
                        selected={webhookLogsPerPage === value}
                        onClick={() => handleWebhookLogsPerPageChange(value)}
                      >
                        {value}
                      </PageSizeButton>
                    ))}
                  </FlexDiv>

                  <FlexDiv align="center" gap={8}>
                    <AppText fontSize={12} fontWeight={400} style={{ marginRight: "8px" }}>
                      Page {webhookLogsPage} of {getWebhookLogsMaxPage()}
                    </AppText>
                    <PhoenixIcon
                      svg={arrow_left}
                      size={20}
                      variant={webhookLogsPage === 1 || webhookLogsLoading ? "neutral" : "brand"}
                      pointer={webhookLogsPage !== 1 && !webhookLogsLoading}
                      onClick={handleWebhookLogsPrevPage}
                      style={{ margin: "0 4px" }}
                    />
                    <PhoenixIcon
                      svg={arrow_right}
                      size={20}
                      variant={
                        webhookLogsLoading || webhookLogsData?.fetchWebhookLogs?.length < webhookLogsPerPage
                          ? "neutral"
                          : "brand"
                      }
                      pointer={!webhookLogsLoading && webhookLogsData?.fetchWebhookLogs?.length === webhookLogsPerPage}
                      onClick={handleWebhookLogsNextPage}
                      style={{ margin: "0 4px" }}
                    />
                  </FlexDiv>
                </TableFooter>
              </WebhookLogsContainer>
            </WebhookTabContainer>
          </WebhookPageWrapper>
        </TabContentWrapper>

        {/* Webhook Log Details Modal */}
        {showWebhookLogDetailsModal && selectedWebhookLog && (
          <Modal
            open={showWebhookLogDetailsModal}
            onClose={() => setShowWebhookLogDetailsModal(false)}
            popupAnimation
            width={700}
          >
            <LogDetailsContent>
              <ModalHeader>
                <AppText fontSize={18} fontWeight={600}>
                  Webhook Log Details
                </AppText>
              </ModalHeader>
              <LogDetailsBody>
                <LogDetailSection>
                  <LogDetailLabel>Log ID</LogDetailLabel>
                  <LogDetailValue>{selectedWebhookLog.id}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Date and Time</LogDetailLabel>
                  <LogDetailValue>{moment(selectedWebhookLog.created_at).format("MM/DD/YYYY HH:mm:ss")}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Method</LogDetailLabel>
                  <MethodBadge
                    color={
                      selectedWebhookLog.method === "GET"
                        ? theme.PRIMARY600
                        : selectedWebhookLog.method === "POST"
                        ? theme.SUCCESS700
                        : selectedWebhookLog.method === "PUT"
                        ? theme.WARNING600
                        : selectedWebhookLog.method === "PATCH"
                        ? theme.PRIMARY700
                        : selectedWebhookLog.method === "DELETE"
                        ? theme.DANGER700
                        : theme.NEUTRAL600
                    }
                  >
                    {selectedWebhookLog.method}
                  </MethodBadge>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Webhook URL</LogDetailLabel>
                  <LogDetailValue>{selectedWebhookLog.url}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Status Code</LogDetailLabel>
                  <LogDetailValue>{selectedWebhookLog.response_code}</LogDetailValue>
                </LogDetailSection>
                <LogDetailSection>
                  <LogDetailLabel>Event Types</LogDetailLabel>
                  <LogDetailValue>
                    {(() => {
                      if (Array.isArray(selectedWebhookLog.event_types) && selectedWebhookLog.event_types.length > 0) {
                        const eventTypeDisplay: Record<WebHookEventType, string> = {
                          [WebHookEventType.LeadUpdated]: "Lead Updated",
                          // [WebHookEventType.LeadUpdatedSpecificSystemFields]: "Lead Updated Specific System Fields", // Commented out - not supported for now
                          [WebHookEventType.LeadCreated]: "Lead Created",
                          [WebHookEventType.LeadActivityCreated]: "Lead Activity Created",
                          [WebHookEventType.LeadActivityDemoSetCreated]: "Demo Set",
                          [WebHookEventType.LeadActivityDemoReescheduled]: "Demo Rescheduled",
                          [WebHookEventType.LeadActivityNewCallNote]: "New Call Note",
                          [WebHookEventType.LeadActivityNewSMS]: "New SMS",
                          [WebHookEventType.LeadActivityNewEmail]: "New Email",
                        };

                        const displayNames = selectedWebhookLog.event_types.map((eventType: string) => {
                          return eventTypeDisplay[eventType as WebHookEventType] || eventType;
                        });

                        return displayNames.join(", ");
                      }
                      return "N/A";
                    })()}
                  </LogDetailValue>
                </LogDetailSection>
                <LogDetailBodySection
                  requestBody={selectedWebhookLog.request_body}
                  responseBody={selectedWebhookLog.response_body}
                />
              </LogDetailsBody>
              <ModalActions>
                <PhoenixAppButton
                  variant="brand"
                  buttonType="secondary"
                  onClick={() => setShowWebhookLogDetailsModal(false)}
                  style={{ minWidth: "120px" }}
                >
                  Close
                </PhoenixAppButton>
              </ModalActions>
            </LogDetailsContent>
          </Modal>
        )}
      </TabContent>
    );
  };

  // Construct docs URL based on environment
  const getDocsUrl = () => {
    const baseUrl = "https://api.public";
    const domain = "sellfire.com/documentation";

    // For production, don't include environment in URL
    if (environment === "Production") {
      return `${baseUrl}.${domain}`;
    }

    // For other environments (beta, staging, etc.), include environment in URL
    const envLower = environment.toLowerCase();
    return `${baseUrl}.${envLower}.${domain}`;
  };

  const docs_link = getDocsUrl();

  return (
    <Container>
      <ReactTooltip effect="solid" />
      <HeaderContainer>
        <HeaderContent>
          <AppText fontSize={28} fontWeight={600} lineHeight={36}>
            API
          </AppText>
          <SubTitle fontSize={16} fontWeight={400} lineHeight={24}>
            Manage API keys, monitor request logs, and configure webhooks for real-time updates
          </SubTitle>
        </HeaderContent>
      </HeaderContainer>

      <ApiDocsLinkContainer>
        <DocsLink href={docs_link} target="_blank" rel="noopener noreferrer">
          <PhoenixIcon svg={maximize2} size={16} color={theme.icon.brand.default} alt="documentation" />
          <DocsLinkText>API Documentation</DocsLinkText>
        </DocsLink>
      </ApiDocsLinkContainer>

      <TabsContainer>
        <TabsWrapper>
          <PhoenixTab tabName="API KEYS" selected={activeTab === "keys"} onClick={() => handleTabChange("keys")} />
          <PhoenixTab tabName="API LOGS" selected={activeTab === "logs"} onClick={() => handleTabChange("logs")} />
          <PhoenixTab
            tabName="WEBHOOKS"
            selected={activeTab === "webhooks"}
            onClick={() => handleTabChange("webhooks")}
          />
        </TabsWrapper>
      </TabsContainer>

      {activeTab === "keys" && renderApiKeysTab()}
      {activeTab === "logs" && renderApiLogsTab()}
      {activeTab === "webhooks" && renderWebhooksTab()}
    </Container>
  );
};

// Styled components
const Container = styled.div`
  height: 100%;
  min-height: 100vh; /* Ensure it takes full viewport height */
  background-color: ${theme.WHITE_COLOR};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  margin: 24px;
`;

const HeaderContainer = styled.div`
  padding: 36px 40px 32px;
  background-color: ${theme.fill.neutral.primary};
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 80%;
`;

const SubTitle = styled(AppText)`
  color: ${theme.text.neutral.secondary};
  margin-top: 4px;
  line-height: 1.6;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${theme.border.neutral.secondary};
  margin-top: 0;
  background-color: transparent;
`;

const TabsWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 0 40px;
`;

const TabContent = styled.div`
  padding: 40px 0;
  width: 100%;
  height: calc(100vh - 160px); /* Subtract header + tabs height */
  background-color: #ffffff;
  display: flex;
  justify-content: flex-start;
  overflow-y: auto;
`;

const TabContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0;
  padding: 0 40px 40px 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

// API Keys tab styles
const KeysWarningMessage = styled.div`
  background-color: ${theme.PRIMARY50};
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 32px;
  width: 70%;
  max-width: 1200px;
  border: 1px solid ${theme.border.neutral.secondary};
  align-self: flex-start;
`;

const KeysWarningText = styled(AppText)`
  font-size: 13px;
  line-height: 1.5;
  color: ${theme.text.neutral.primary};
  max-width: 100%;
`;

const TableContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  background: ${theme.WHITE_COLOR};
  border: 1px solid ${theme.border.neutral.secondary};
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
  align-self: flex-start;

  .ag-theme-alpine {
    width: 100%;
    height: 100%;
  }

  .ag-header,
  .ag-header-container,
  .ag-header-row {
    min-height: 40px !important;
    background-color: ${theme.PRIMARY100};
  }

  .ag-header-cell {
    padding: 0px 16px;
    border-bottom: solid 1px ${theme.border.neutral.secondary};
    background-color: ${theme.PRIMARY100};
  }

  /* No need for special center class, everything is left-aligned by default */
  .ag-cell-value,
  .ag-header-cell-label {
    font-size: 12px;
    line-height: 18px;
    color: ${theme.text.neutral.primary};
  }

  .ag-header-cell-label {
    font-weight: 600;
    /* This is the key styling for left alignment */
    justify-content: flex-start;
  }

  .ag-header-cell-text {
    text-transform: capitalize;
    font-weight: 600;
    font-size: 11px;
    line-height: 14px;
  }

  .ag-row-odd {
    background-color: ${theme.PRIMARY50};
  }

  .ag-row-even {
    background-color: ${theme.WHITE_COLOR};
  }

  .ag-cell {
    display: flex;
    align-items: center;
    padding: 0px 16px;
    border-right: none;
    border-bottom: solid 1px ${theme.border.neutral.secondary};
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .ag-cell-value {
    text-align: left;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  width: 100%;
`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 80px;
  width: 100%;
`;

const BottomSpacer = styled.div`
  width: 100%;
  height: 120px; /* Add significant height to ensure content doesn't appear at the bottom */
`;

const ItemsPerPageText = styled(AppText)`
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  margin-right: 8px;
  color: ${theme.text.neutral.secondary};
`;

const PageSizeButton = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  margin-right: 4px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? theme.fill.brand.primary : "transparent")};
  color: ${(props) => (props.selected ? theme.WHITE_COLOR : theme.text.neutral.primary)};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CreateButton = styled(PhoenixAppButton)`
  border-radius: 4px;
`;

const ComingSoonMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background-color: ${theme.surface.neutral.primary};
  border-radius: 8px;
  font-size: 14px;
  color: ${theme.text.neutral.secondary};
  width: 100%;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${theme.surface.neutral.primary};
  border-radius: 8px;
  width: 500px;
  box-shadow: 0px 4px 8px rgba(16, 24, 40, 0.1), 0px 2px 4px rgba(16, 24, 40, 0.06);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 24px;
  border-top: 1px solid ${theme.border.neutral.secondary};
  background-color: ${theme.fill.neutral.primary};
`;

const PermissionSectionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.text.neutral.primary};
  margin-bottom: 16px;
`;

const PermissionOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const PermissionOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${theme.PRIMARY500};
  cursor: pointer;
`;

const PermissionLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const PermissionName = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.text.neutral.primary};
`;

// Delete Modal Styles
const DeleteModalContent = styled.div`
  width: 100%;
  padding: 0;
`;

const DeleteModalBody = styled.div`
  padding: 24px;
`;

const DeleteModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${theme.border.neutral.secondary};
`;

// API Logs tab styles
const LogsExplanationContainer = styled.div`
  background-color: ${theme.fill.neutral.primary};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const ExplanationTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.text.neutral.primary};
  margin-bottom: 8px;
`;

const ExplanationText = styled.div`
  font-size: 14px;
  color: ${theme.text.neutral.secondary};
  line-height: 1.5;
`;

const DateFilterContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
  padding: 12px;
  background: ${theme.fill.neutral.primary};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
`;

const DateFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const DatePickerContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const FilterLabel = styled.div`
  position: absolute;
  left: 0;
  top: 9px;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.text.neutral.primary};
  white-space: nowrap;
  z-index: 1;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const FilterButton = styled(PhoenixAppButton)`
  min-width: 110px;
  height: 36px;
`;

const ExportButton = styled(PhoenixAppButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;
  height: 36px;
  border-radius: 4px;
`;

// Log Details Modal Styles
const LogDetailsContent = styled.div`
  width: 100%;
  padding: 0;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const LogDetailsBody = styled.div`
  padding: 24px;
  max-height: calc(90vh - 130px);
  overflow-y: auto;
  flex: 1;
`;

const LogDetailSection = styled.div`
  margin-bottom: 24px;
`;

const LogDetailLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.text.neutral.secondary};
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const LogDetailValue = styled.div`
  font-size: 14px;
  color: ${theme.text.neutral.primary};
  word-break: break-word;
  max-width: 100%;
  overflow-wrap: break-word;
  padding: 8px;
  background-color: ${theme.fill.neutral.secondary};
  border-radius: 4px;
`;

const CodeBlock = styled.pre`
  background-color: ${theme.PRIMARY50};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 4px;
  padding: 12px;
  font-family: "Roboto Mono", monospace;
  font-size: 12px;
  color: ${theme.text.neutral.primary};
  overflow-x: auto;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.5;
`;

const LogDetailBodySection = ({
  requestBody,
  responseBody,
  body,
}: {
  requestBody?: string;
  responseBody?: string;
  body?: string;
}) => {
  const handleCopy = (text: string | undefined, type: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    appToast(`${type} copied to clipboard`);
  };

  // Helper function to safely parse and format JSON, and remove backslashes
  const formatJson = (jsonString: string | undefined) => {
    try {
      if (!jsonString || typeof jsonString !== "string") return "";
      let cleaned = String(jsonString).replace(/\\/g, ""); // Remove all backslashes
      let trimmed = cleaned.trim();
      // If the string is wrapped in quotes, remove them
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        trimmed = trimmed.slice(1, -1);
      }
      // Try to parse if it looks like JSON
      if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
          return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch (e) {
          return trimmed;
        }
      }
      return trimmed;
    } catch (err) {
      return "";
    }
  };

  if (body) {
    return (
      <LogDetailSection>
        <LogDetailHeaderWrapper>
          <LogDetailLabel>Body</LogDetailLabel>
          <PhoenixIcon
            svg={copy}
            size={16}
            color={theme.icon.brand.default}
            hoverColor={theme.icon.brand.hover}
            alt="copy"
            pointer
            onClick={() => handleCopy(body, "Body")}
          />
        </LogDetailHeaderWrapper>
        <CodeBlock>{formatJson(body)}</CodeBlock>
      </LogDetailSection>
    );
  }

  return (
    <>
      <LogDetailSection>
        <LogDetailHeaderWrapper>
          <LogDetailLabel>Request Body</LogDetailLabel>
          <PhoenixIcon
            svg={copy}
            size={16}
            color={theme.icon.brand.default}
            hoverColor={theme.icon.brand.hover}
            alt="copy"
            pointer
            onClick={() => handleCopy(requestBody, "Request body")}
          />
        </LogDetailHeaderWrapper>
        <CodeBlock>{formatJson(requestBody)}</CodeBlock>
      </LogDetailSection>

      <LogDetailSection>
        <LogDetailHeaderWrapper>
          <LogDetailLabel>Response Body</LogDetailLabel>
          <PhoenixIcon
            svg={copy}
            size={16}
            color={theme.icon.brand.default}
            hoverColor={theme.icon.brand.hover}
            alt="copy"
            pointer
            onClick={() => handleCopy(responseBody, "Response body")}
          />
        </LogDetailHeaderWrapper>
        <CodeBlock>{formatJson(responseBody)}</CodeBlock>
      </LogDetailSection>
    </>
  );
};

const LogDetailHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

// Webhook tab styles
const WebhookTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  padding-bottom: 80px; /* Add more padding at the bottom */
`;

const WebhookFormContainer = styled.div`
  background-color: ${theme.fill.neutral.primary};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
`;

const WebhookFormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const WebhookDescription = styled(AppText)`
  font-size: 14px;
  color: ${theme.text.neutral.secondary};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.text.neutral.primary};
  margin-bottom: 8px;
`;

const FormHelpText = styled.div`
  font-size: 12px;
  color: ${theme.text.neutral.secondary};
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const WebhookSubscriptionsContainer = styled.div`
  background-color: ${theme.fill.neutral.primary};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
`;

const WebhookLogsContainer = styled.div`
  background-color: ${theme.fill.neutral.primary};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
`;

const WebhookAlert = styled.div`
  background-color: ${theme.fill.warning.tertiary};
  border: 1px solid ${theme.border.warning.primary};
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const EmptySubscriptions = styled.div`
  background-color: ${theme.fill.neutral.secondary};
  border-radius: 4px;
  padding: 24px;
  text-align: center;
`;

const SubscriptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubscriptionItem = styled.div`
  background-color: ${theme.fill.neutral.secondary};
  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  padding: 16px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${theme.border.neutral.tertiary};
  }
`;

const SubscriptionItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EventTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background-color: ${theme.fill.brand.secondary};
  color: ${theme.text.brand.primary};
  font-weight: 600;
  font-size: 14px;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SubscriptionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UnsubscribeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-family: inherit;
  color: ${theme.text.danger.primary};
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.fill.danger.tertiary};
  }
`;

const UnsubscribeText = styled.span`
  font-size: 12px;
  font-weight: 500;
  margin-left: 4px;
`;

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const WebhookEventSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const EventOption = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? theme.fill.brand.primary : theme.fill.neutral.secondary)};
  color: ${(props) => (props.selected ? theme.text.neutral.inverse : theme.text.neutral.primary)};
  border: 1px solid ${(props) => (props.selected ? theme.border.brand.primary : theme.border.neutral.secondary)};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? theme.fill.brand.primary : theme.fill.neutral.tertiary)};
  }
`;

// Add global documentation link container
const ApiDocsLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 16px 40px 0;
`;
